import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { bookingApi } from '../../services/bookingApi';
import { useSquareService, useTaxRates } from '../../hooks/useBookingData';
import { rateForCity } from '../../lib/tax';
import { StepVariation } from './StepVariation';
import { StepDateTime } from './StepDateTime';
import { StepDetails } from './StepDetails';
import { StepPayment } from './StepPayment';
import { StepConfirmation } from './StepConfirmation';
import { StepService } from './StepService';
import { StepAddons } from './StepAddons';

const DEPOSIT_CENTS = 5000; // Must match backend DEPOSIT_CENTS in routes/booking.py

// Outer component — owns only the Dialog open/close lifecycle.
// The inner session is mounted fresh each time the modal opens, so all
// step state is naturally reset without explicit reset code.
export const BookingFlow = ({ open, onClose, serviceName, promo = null }) => {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-white border-neutral-200 border-t-4 border-t-red-600 text-neutral-900 w-full max-w-3xl h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[92vh] p-4 pt-6 sm:p-6 rounded-none sm:rounded-lg overflow-y-auto overflow-x-hidden content-start grid-cols-[minmax(0,1fr)] [&>button:last-child]:p-3 sm:[&>button:last-child]:p-2 [&>button:last-child]:right-1 [&>button:last-child]:top-2"
        data-testid="booking-modal"
        aria-describedby={undefined}
      >
        {open && <BookingEntry serviceName={serviceName} onClose={onClose} promo={promo} />}
      </DialogContent>
    </Dialog>
  );
};

// Without a preset service, the customer picks one from the Square catalog first.
const BookingEntry = ({ serviceName, onClose, promo }) => {
  const [picked, setPicked] = useState(null);
  const chosen = serviceName || picked;

  if (!chosen) {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="font-display italic uppercase text-3xl font-extrabold text-neutral-950 pr-8">
            Book Your Detail
          </DialogTitle>
        </DialogHeader>
        <StepService onSelect={setPicked} />
      </>
    );
  }
  return (
    <BookingSession
      key={chosen}
      serviceName={chosen}
      onClose={onClose}
      promo={promo}
      onChangeService={serviceName ? null : () => setPicked(null)}
    />
  );
};

// All booking session state lives here. Mounts on open, unmounts on close.
const BookingSession = ({ serviceName, onClose, onChangeService, promo }) => {
  const { service, loadError, loading: loadingService } = useSquareService(serviceName);
  const { taxData, taxError } = useTaxRates();

  const [stepIdx, setStepIdx] = useState(0);
  const [pickedVariation, setPickedVariation] = useState(null);
  const [addonQty, setAddonQty] = useState({});
  const [slot, setSlot] = useState(null);
  const [details, setDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [result, setResult] = useState(null);

  // If the service has a single variation, use it implicitly; otherwise
  // wait for the user to pick one in StepVariation. This avoids a redundant
  // useEffect that would just sync state with derived data.
  const variation = pickedVariation
    || (service && service.variations.length === 1 ? service.variations[0] : null);

  // Dynamic step list — skip variation step if service has only one
  const steps = useMemo(() => {
    if (!service) return [];
    const arr = [];
    if (service.variations.length > 1) arr.push('Package');
    if (service.addons?.length) arr.push('Add-ons');
    arr.push('Date & Time', 'Your Details', 'Payment');
    return arr;
  }, [service]);

  const totalUiSteps = steps.length;
  const currentStepName = steps[stepIdx];
  const showConfirmation = !!result;

  const handleVariation = useCallback((v) => {
    setPickedVariation(v);
    setStepIdx((i) => i + 1);
  }, []);
  const handleAddons = useCallback((q) => {
    setAddonQty(q);
    setStepIdx((i) => i + 1);
  }, []);
  const handleDateTime = useCallback((s) => {
    setSlot(s);
    setStepIdx((i) => i + 1);
  }, []);
  const handleDetails = useCallback((d) => {
    setDetails(d);
    setStepIdx((i) => i + 1);
  }, []);
  const handleBack = useCallback(() => setStepIdx((i) => Math.max(0, i - 1)), []);

  // Stable prop for StepDateTime so it doesn't re-render on every parent render
  const datetimeService = useMemo(
    () => (variation
      ? { variation_id: variation.variation_id, duration_minutes: variation.duration_minutes }
      : null),
    [variation],
  );

  const addonLines = useMemo(() => (service?.addons || []).flatMap((g) => g.options
    .filter((o) => addonQty[o.modifier_id])
    .map((o) => ({ modifier_id: o.modifier_id, group: g.name, quantity: addonQty[o.modifier_id], total_cents: o.price_cents * addonQty[o.modifier_id] }))), [service, addonQty]);
  const addonsCents = addonLines.reduce((sum, a) => sum + a.total_cents, 0);
  const discountCents = (service && promo?.services?.[service.name.toLowerCase()]) || 0;

  const handlePayment = useCallback(async (token, cardholderName) => {
    if (!service || !variation || !slot || !details) return;
    setSubmitting(true);
    setPaymentError(null);
    try {
      const booking = await bookingApi.createBooking({
        given_name: details.given_name,
        family_name: details.family_name,
        email: details.email,
        phone: details.phone,
        customer_note: details.customer_note || null,
        cardholder_name: cardholderName || null,
        service_address: details.service_address.trim(),
        service_city: details.service_city,
        service_zip: details.service_zip,
        variation_id: variation.variation_id,
        variation_version: variation.variation_version,
        duration_minutes: variation.duration_minutes,
        price_cents: variation.price_cents,
        service_name: service.name,
        start_at: slot.start_at,
        team_member_id: slot.team_member_id,
        source_id: token,
        addons: addonLines.map((a) => ({ modifier_id: a.modifier_id, quantity: a.quantity })),
        promo: discountCents ? promo.code : null,
      });
      setResult(booking);
      toast.success('Booking confirmed!');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Booking failed. Please try again or call (505) 595-9416.';
      setPaymentError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [service, variation, slot, details, addonLines, discountCents, promo]);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display italic uppercase text-3xl font-extrabold text-neutral-950 pr-8">
          Book {service ? service.name : serviceName}
        </DialogTitle>
        {variation && (
          <p className="text-neutral-600 text-sm">
            {variation.duration_minutes} min • ${((variation.price_cents + addonsCents - discountCents) / 100).toFixed(2)}{addonsCents ? ' incl. add-ons' : ''}{discountCents ? ` (${promo.label} applied)` : ''} + tax • <span className="text-red-600 font-semibold">$50 deposit + local tax to book</span>
          </p>
        )}
        {onChangeService && !showConfirmation && (
          <button
            type="button"
            onClick={onChangeService}
            className="text-sm text-red-600 hover:text-red-700 font-semibold underline underline-offset-4 w-fit"
            data-testid="change-service-btn"
          >
            Change service
          </button>
        )}
      </DialogHeader>

      {loadingService && (
        <div className="py-12 flex items-center justify-center text-neutral-600">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading service…
        </div>
      )}

      {!loadingService && loadError && (
        <div className="py-8 text-center" data-testid="booking-load-error">
          <AlertCircle className="w-10 h-10 text-red-600 mx-auto mb-3" />
          <p className="text-neutral-700">{loadError}</p>
        </div>
      )}

      {/* Step indicator */}
      {!loadingService && !loadError && service && !showConfirmation && totalUiSteps > 0 && (
        <div className="flex items-center gap-1.5 sm:gap-2 my-2 min-w-0" data-testid="booking-step-indicator">
          {steps.map((label, idx) => {
            const isActive = stepIdx === idx;
            const isDone = stepIdx > idx;
            return (
              <div key={label} className={`flex items-center gap-1.5 sm:gap-2 min-w-0 ${isActive ? 'flex-[2]' : 'flex-1'}`}>
                <div className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold border transition-colors ${
                  isDone ? 'bg-green-50 border-green-500 text-green-700'
                    : isActive ? 'bg-red-600 border-red-600 text-white'
                    : 'bg-neutral-100 border-neutral-200 text-neutral-400'
                }`}>{idx + 1}</div>
                <span className={`text-xs uppercase tracking-wider whitespace-nowrap truncate ${isActive ? 'text-neutral-950 font-semibold' : 'hidden sm:inline text-neutral-400'}`}>{label}</span>
                {idx < totalUiSteps - 1 && <div className="flex-1 min-w-2 h-px bg-neutral-200" />}
              </div>
            );
          })}
        </div>
      )}

      {/* Steps */}
      {!loadingService && !loadError && service && !showConfirmation && (
        <>
          {currentStepName === 'Package' && (
            <StepVariation service={service} onSelect={handleVariation} />
          )}
          {currentStepName === 'Add-ons' && (
            <StepAddons service={service} initial={addonQty} onContinue={handleAddons} onBack={stepIdx > 0 ? handleBack : null} />
          )}
          {currentStepName === 'Date & Time' && datetimeService && (
            <StepDateTime
              service={datetimeService}
              onSelect={handleDateTime}
            />
          )}
          {currentStepName === 'Your Details' && (
            <>
              {taxError && <p className="text-red-600 text-sm" data-testid="tax-load-error">{taxError}</p>}
              <StepDetails initial={details} onSubmit={handleDetails} onBack={handleBack} taxData={taxData} />
            </>
          )}
          {currentStepName === 'Payment' && variation && details && (
            <StepPayment
              depositCents={DEPOSIT_CENTS}
              totalCents={variation.price_cents + addonsCents - discountCents}
              discountCents={discountCents}
              promoLabel={promo?.label}
              baseCents={variation.price_cents}
              addonLines={addonLines}
              taxRate={rateForCity(details.service_city, taxData)}
              defaultCardholderName={details ? `${details.given_name} ${details.family_name}`.trim() : ''}
              onTokenized={handlePayment}
              onBack={handleBack}
              submitting={submitting}
              errorMessage={paymentError}
            />
          )}
        </>
      )}

      {showConfirmation && details && (
        <StepConfirmation result={result} customerEmail={details.email} onClose={onClose} />
      )}
    </>
  );
};
